import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { 
  ArrowLeft, Eye, TrendingUp, Heart, RotateCcw, Users, 
  Sparkles, ChevronRight, Lock, Crown, MessageCircle,
  ArrowUpRight, ArrowDownRight, Minus, Loader2, RefreshCw,
  CheckCircle, Clock, Zap
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatLocation } from "@/utils/recoveryTerminology";

const InsightsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [engagement, setEngagement] = useState(null);
  const [profileViews, setProfileViews] = useState(null);
  const [whoInterested, setWhoInterested] = useState(null);
  const [secondChance, setSecondChance] = useState(null);
  const [reconsidering, setReconsidering] = useState(null);

  const isPremium = ["recovery_plus", "recovery_elite"].includes(
    user?.subscription_tier
  );

  useEffect(() => {
    if (isPremium) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [engagementRes, viewsRes, interestedRes, secondChanceRes] = await Promise.all([
        api.getEngagementStats(),
        api.getProfileViews(7),
        api.getWhoInterested(),
        api.getSecondChance()
      ]);
      setEngagement(engagementRes.data);
      setProfileViews(viewsRes.data);
      setWhoInterested(interestedRes.data);
      setSecondChance(secondChanceRes.data);
    } catch (error) {
      console.error("Failed to load insights:", error);
      if (error.response?.status === 403) {
        toast.error("Upgrade to Recovery+ to access insights!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReconsider = async (matchId, userName) => {
    setReconsidering(matchId);
    try {
      const response = await api.reconsiderProfile(matchId);
      if (response.data.is_mutual_match) {
        toast.success(`🎉 It's a match with ${userName}!`);
      } else {
        toast.success("Interest expressed! Waiting for their response.");
      }
      // Refresh second chance list
      const res = await api.getSecondChance();
      setSecondChance(res.data);
    } catch (error) {
      toast.error("Failed to reconsider profile");
    } finally {
      setReconsidering(null);
    }
  };

  // Premium gate screen
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="insights-locked">
        <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Profile Insights</h1>
          </div>
        </div>
        
        <div className="p-6 text-center mt-12">
          <div className="w-20 h-20 bg-[#FAB710]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[#FAB710]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Premium Feature</h2>
          <p className="text-gray-600 mb-6 max-w-sm mx-auto">
            Upgrade to Recovery+ to see who viewed your profile, engagement stats, and get a second chance with passed matches.
          </p>
          <Button
            onClick={() => navigate("/subscription")}
            className="bg-[#FAB710] hover:bg-[#e8a50f] text-white font-semibold px-8"
            data-testid="upgrade-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Recovery+
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FAB710]" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "views", label: "Who Viewed", icon: Eye, badge: profileViews?.unique_viewers },
    { id: "interested", label: "Interested", icon: Heart, badge: whoInterested?.pending_response },
    { id: "second-chance", label: "Second Chance", icon: RotateCcw, badge: secondChance?.instant_matches_available }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="insights-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Profile Insights</h1>
          </div>
          <button 
            onClick={fetchAllData}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"
          >
            <RefreshCw className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{engagement?.profile_views?.this_week || 0}</p>
            <p className="text-white/70 text-xs">Views (7d)</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{whoInterested?.pending_response || 0}</p>
            <p className="text-white/70 text-xs">Interested</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-[#FAB710]">{secondChance?.instant_matches_available || 0}</p>
            <p className="text-white/70 text-xs">Instant Matches</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="flex overflow-x-auto px-2 py-2 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-[#1C3A7A] text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className={`min-w-[20px] h-5 text-xs font-bold rounded-full flex items-center justify-center ${
                  activeTab === tab.id ? "bg-[#FAB710] text-white" : "bg-[#FAB710] text-white"
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Overview Tab */}
        {activeTab === "overview" && engagement && (
          <>
            {/* View Trend */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#1C3A7A]" />
                Profile Views
              </h3>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-4xl font-bold text-gray-800">{engagement.profile_views?.this_week || 0}</p>
                  <p className="text-gray-500 text-sm">views this week</p>
                </div>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  engagement.profile_views?.trend_direction === "up" 
                    ? "bg-green-100 text-green-700"
                    : engagement.profile_views?.trend_direction === "down"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
                }`}>
                  {engagement.profile_views?.trend_direction === "up" && <ArrowUpRight className="w-4 h-4" />}
                  {engagement.profile_views?.trend_direction === "down" && <ArrowDownRight className="w-4 h-4" />}
                  {engagement.profile_views?.trend_direction === "stable" && <Minus className="w-4 h-4" />}
                  {engagement.profile_views?.trend_percent || 0}%
                </div>
              </div>
              
              {/* Mini Chart */}
              <div className="flex items-end gap-1 h-16">
                {engagement.daily_views?.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-[#1C3A7A]/20 rounded-t"
                      style={{ 
                        height: `${Math.max(4, (day.views / Math.max(...engagement.daily_views.map(d => d.views), 1)) * 60)}px`,
                        backgroundColor: idx === engagement.daily_views.length - 1 ? "#1C3A7A" : undefined
                      }}
                    />
                    <span className="text-[10px] text-gray-400 mt-1">
                      {new Date(day.date).toLocaleDateString('en', { weekday: 'short' }).charAt(0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement Stats */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FAB710]" />
                Your Performance
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-[#1C3A7A]">{engagement.visibility?.times_shown_in_matches || 0}</p>
                  <p className="text-gray-600 text-sm">Times shown</p>
                </div>
                <div className="bg-pink-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-pink-600">{engagement.visibility?.interest_rate || 0}%</p>
                  <p className="text-gray-600 text-sm">Interest rate</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-green-600">{engagement.matching?.mutual_matches || 0}</p>
                  <p className="text-gray-600 text-sm">Mutual matches</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">{engagement.matching?.match_rate || 0}%</p>
                  <p className="text-gray-600 text-sm">Match rate</p>
                </div>
              </div>
            </div>

            {/* Tips */}
            {engagement.tips && engagement.tips.length > 0 && (
              <div className="bg-gradient-to-br from-[#FAB710]/10 to-[#FAB710]/5 rounded-2xl p-5">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#FAB710]" />
                  Tips for You
                </h3>
                <ul className="space-y-2">
                  {engagement.tips.map((tip, idx) => (
                    <li key={idx} className="text-gray-700 text-sm">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Who Viewed Tab */}
        {activeTab === "views" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-600 text-sm">
                {profileViews?.unique_viewers || 0} people viewed your profile
              </p>
            </div>
            
            {profileViews?.viewers?.length > 0 ? (
              profileViews.viewers.map((viewer, idx) => (
                <div 
                  key={idx}
                  className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-4"
                  data-testid={`viewer-${idx}`}
                >
                  <div className="relative">
                    <img
                      src={viewer.user.photos?.[0] || "/default-avatar.png"}
                      alt={viewer.user.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    {viewer.expressed_interest && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                        <Heart className="w-3 h-3 text-white fill-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800">{viewer.user.name}, {viewer.user.age}</p>
                      {viewer.is_mutual_match && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Matched</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">{formatLocation(viewer.user.location)}</p>
                    <p className="text-gray-400 text-xs">
                      Viewed {viewer.view_count}x • Last {new Date(viewer.last_viewed).toLocaleDateString()}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No profile views yet</p>
                <p className="text-gray-400 text-sm">Keep your profile active!</p>
              </div>
            )}
          </div>
        )}

        {/* Who's Interested Tab */}
        {activeTab === "interested" && (
          <div className="space-y-3">
            {whoInterested?.pending_response > 0 && (
              <div className="bg-pink-50 rounded-xl p-4 mb-4">
                <p className="text-pink-700 font-medium">
                  💝 {whoInterested.pending_response} people are waiting for your response!
                </p>
              </div>
            )}
            
            {whoInterested?.interested_users?.length > 0 ? (
              whoInterested.interested_users.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-white rounded-xl p-4 shadow-sm"
                  data-testid={`interested-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={item.user.photos?.[0] || "/default-avatar.png"}
                      alt={item.user.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{item.user.name}, {item.user.age}</p>
                      <p className="text-gray-500 text-sm">{formatLocation(item.user.location)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs bg-[#1C3A7A]/10 text-[#1C3A7A] px-2 py-0.5 rounded-full">
                          {item.compatibility_score}% match
                        </span>
                        {item.your_response === "pending" && (
                          <span className="text-xs bg-[#FAB710]/20 text-[#FAB710] px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Waiting
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mt-3 line-clamp-2">{item.user.bio}</p>
                  <Button
                    onClick={() => navigate(`/matches`)}
                    className="w-full mt-3 bg-[#1C3A7A] hover:bg-[#2a4f9e]"
                    size="sm"
                  >
                    View in Matches
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No one has expressed interest yet</p>
                <p className="text-gray-400 text-sm">Keep checking your daily matches!</p>
              </div>
            )}
          </div>
        )}

        {/* Second Chance Tab */}
        {activeTab === "second-chance" && (
          <div className="space-y-3">
            {secondChance?.instant_matches_available > 0 && (
              <div className="bg-gradient-to-r from-[#FAB710] to-[#e8a50f] rounded-xl p-4 mb-4">
                <p className="text-white font-medium">
                  🎯 {secondChance.instant_matches_available} instant matches waiting! They already expressed interest.
                </p>
              </div>
            )}
            
            {secondChance?.profiles?.length > 0 ? (
              secondChance.profiles.map((item, idx) => (
                <div 
                  key={idx}
                  className={`bg-white rounded-xl p-4 shadow-sm ${item.instant_match ? 'ring-2 ring-[#FAB710]' : ''}`}
                  data-testid={`second-chance-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={item.user.photos?.[0] || "/default-avatar.png"}
                        alt={item.user.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      {item.instant_match && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-[#FAB710] rounded-full flex items-center justify-center">
                          <Zap className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">{item.user.name}, {item.user.age}</p>
                        {item.instant_match && (
                          <span className="text-xs bg-[#FAB710]/20 text-[#FAB710] px-2 py-0.5 rounded-full font-medium">
                            Instant Match!
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm">{formatLocation(item.user.location)}</p>
                      <span className="text-xs text-gray-400">
                        Passed on {new Date(item.passed_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  {item.user.bio && (
                    <p className="text-gray-600 text-sm mt-3 line-clamp-2">{item.user.bio}</p>
                  )}
                  
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handleReconsider(item.match_id, item.user.name)}
                      disabled={reconsidering === item.match_id}
                      className={`flex-1 ${item.instant_match ? 'bg-[#FAB710] hover:bg-[#e8a50f]' : 'bg-[#1C3A7A] hover:bg-[#2a4f9e]'}`}
                      size="sm"
                    >
                      {reconsidering === item.match_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          {item.instant_match ? "Match Now!" : "Reconsider"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No passed profiles to reconsider</p>
                <p className="text-gray-400 text-sm">When you pass on someone, they&apos;ll appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsPage;
